/* eslint-disable */
/**
 * wallet — real-time SMS credit balance for the header pill + point-of-action
 * reinforcement (campaign send confirmation, SMS composer guidance).
 *
 * Data contract (written by the backend):
 *   Firestore doc siteWallet/{siteID} = {
 *     siteId, creditsAvailable, freeAllowance, freeUsed, freeRemainingCredits,
 *     paidBalanceUsd, paidCredits, creditPriceUsd, resetDate, walletEnabled, updatedAt,
 *     channels: { sms: {…summary}, email: {…summary} }   // per-channel breakdown (E4)
 *   }
 *   The top-level fields are the SMS pool (kept flat for backward-compat + the optimistic
 *   draw); `channels.sms` mirrors them and `channels.email` is the separate email pool
 *   (10k free/mo @ $0.005). creditsAvailable = freeRemainingCredits + paidCredits (the ONE
 *   glance number, per channel).
 *   HTTP POST /get-credit-balance { siteID } → the same summary (minus walletEnabled/updatedAt)
 *
 * Real-time model MIRRORS conversationsV2 / notifications: seed once over HTTP for an
 * instant value, then safeSnapshot (store/firestoreListen.js) the doc using the modular Firebase
 * API pulled off rootState (doc, db) — the same handles firebase.config.js exports into
 * root state. The unsubscribe fn is stored AND registered with
 * customer/addFirestoreListener, so logout tears it down with every other listener.
 */
import axios from 'axios';
import { drawCredits } from '@/utils/walletCredits';
import { negativeChannels, negativeBannerCopy } from '@/utils/negativeBalanceBanner';
import { safeSnapshot } from '@/store/firestoreListen';
import { openBillingPortal as openPortalUrl } from '@/api/billing';
import { billingErrorMessage } from '@/utils/billing/billingView';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

function initialState() {
  return {
    siteID: null,
    creditsAvailable: 0,
    freeAllowance: 0,
    freeUsed: 0,
    freeRemainingCredits: 0,
    paidBalanceUsd: 0,
    paidCredits: 0,
    creditPriceUsd: 0.025,
    resetDate: null,

    // Per-channel breakdown (E4) for the multi-channel header pill + the My Account
    // Credits page. `sms` mirrors the flat top-level fields; `email` is the separate
    // email pool (display-only until E3 ships). null until a summary/doc has loaded.
    channels: { sms: null, email: null },

    // Server-authoritative low-balance flags projected onto the wallet doc
    // (siteWallet/{siteID}.lowBalanceAlert). Each is true when THAT channel is low
    // AND unprotected (auto-reload off or failing) — the decision is made server-side
    // (creditWallet.shouldAlertLowBalance) so the header low-balance banner never
    // re-derives it. Absent on the HTTP seed (get-credit-balance omits it); populated
    // by the Firestore snapshot. Defaults false so the banner stays hidden until set.
    lowBalanceAlert: { sms: false, email: false },

    // Below-zero counterpart (siteWallet/{siteID}.negativeBalanceAlert, 2026-10-08): true when THAT
    // channel is under $0 AND nothing will refill it (auto-reload off / failed / monthly limit).
    // graceUsd = how far below zero sends continue before they stop. Same capture rules as above.
    negativeBalanceAlert: { sms: false, email: false },
    graceUsd: { sms: 5, email: 5 },

    loading: false,
    subscribed: false,
    hasBalance: false, // a summary/doc has loaded at least once
    unsub: null,       // Firestore unsubscribe fn

    // Auto-reload settings (My Account → SMS Credits): the persisted config the
    // backend returns from get-/save-reload-settings.
    // { enabled, triggerUsd, amountUsd, monthlyCapUsd|null } or null before it loads.
    reloadConfig: null,

    // Credit-activity ledger (My Account → SMS Credits "Credit activity" table),
    // paginated via get-credit-activity { limit, offset }.
    activity: [],
    activityLoading: false,
    activityHasMore: false,

    // ---- EMAIL pool counterparts (E3) ----
    // The SMS auto-reload config + activity above stay flat & byte-identical (the pill and
    // the existing SmsCreditsTab SMS path read them directly). Email gets its OWN parallel
    // state so the two pools never clobber each other, threaded via the `product: 'email'`
    // param on the money actions. null/empty until the email pool is loaded.
    emailReloadConfig: null,
    emailActivity: [],
    emailActivityLoading: false,
    emailActivityHasMore: false,

    // ---- USAGE REPORT (My Account → Credits → Usage) ----
    // Answers "where did my credits go?" and "show me every message". Distinct from `activity`
    // above: that is the money-IN ledger (top-ups/reloads); this is per-message CONSUMPTION.
    //
    // usageMonth: 'YYYY-MM' or null = current billing month. Null is meaningful, not just a default —
    // the backend only returns a balance/remaining for the current month (a past month has no
    // "remaining"), so the UI shows the reconciling header line only when this is null.
    usageMonth: null,
    // Keyed by channel so the two pools never clobber each other (same reasoning as the email
    // state above). { sms: {...}, email: {...} } | each: { creditsUsed, balance, breakdown }
    usageSummary: { sms: null, email: null },
    usageSummaryLoading: false,
    // The drill-down message log — ONE active view (a click on a breakdown row filters it), which is
    // why it isn't keyed by channel: the channel is part of the active query.
    usageLog: { rows: [], page: 1, total: 0, hasMore: false, channel: 'sms', feature: null, direction: null },
    usageLogLoading: false,
  };
}

const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

export default {
  namespaced: true,
  state: initialState(),

  getters: {
    creditsAvailable: (s) => s.creditsAvailable,
    hasBalance: (s) => s.hasBalance,
    // Per-channel summaries for the multi-channel pill + Credits page (null until loaded).
    smsChannel: (s) => (s.channels && s.channels.sms) || null,
    emailChannel: (s) => (s.channels && s.channels.email) || null,

    // Server-authoritative low-balance flags for the header banner. Returns booleans
    // (defaulting false when the doc omits the field) plus `any` = "show the banner".
    lowBalanceAlert: (s) => {
      const a = s.lowBalanceAlert || {};
      const sms = !!a.sms;
      const email = !!a.email;
      return { sms, email, any: sms || email };
    },

    // Below-zero channels ({ sms, email, any }) and the banner copy (null when none) — the decision
    // and the wording live in utils/negativeBalanceBanner (pure, node-tested on the real file).
    negativeBalance: (s) => negativeChannels(s),
    negativeBalanceCopy: (s) => negativeBannerCopy(s),
  },

  mutations: {
    setSiteID(state, siteID) { state.siteID = siteID || null; },

    // Apply a balance summary from EITHER the HTTP seed or a Firestore snapshot.
    // creditsAvailable is authoritative from the payload when present; else derived.
    setBalance(state, payload) {
      if (!payload) return;
      state.freeAllowance = num(payload.freeAllowance, state.freeAllowance);
      state.freeUsed = num(payload.freeUsed, state.freeUsed);
      state.freeRemainingCredits = num(payload.freeRemainingCredits, state.freeRemainingCredits);
      state.paidBalanceUsd = num(payload.paidBalanceUsd, state.paidBalanceUsd);
      state.paidCredits = num(payload.paidCredits, state.paidCredits);
      state.creditPriceUsd = num(payload.creditPriceUsd, state.creditPriceUsd || 0.025);
      if (payload.resetDate != null) state.resetDate = payload.resetDate;
      state.creditsAvailable = payload.creditsAvailable != null
        ? num(payload.creditsAvailable)
        : state.freeRemainingCredits + state.paidCredits;

      // Per-channel breakdown (E4). Merge so a payload carrying only one channel never
      // wipes the other; each channel summary is stored raw for the pill / Credits page.
      if (payload.channels && typeof payload.channels === 'object') {
        state.channels = {
          sms: payload.channels.sms || state.channels.sms,
          email: payload.channels.email || state.channels.email,
        };
      }

      // Low-balance flags — ONLY overwrite when the payload carries the field, so the HTTP
      // seed (which omits lowBalanceAlert) never clobbers a value the Firestore snapshot set.
      if (payload.lowBalanceAlert && typeof payload.lowBalanceAlert === 'object') {
        state.lowBalanceAlert = {
          sms: !!payload.lowBalanceAlert.sms,
          email: !!payload.lowBalanceAlert.email,
        };
      }
      if (payload.negativeBalanceAlert && typeof payload.negativeBalanceAlert === 'object') {
        state.negativeBalanceAlert = {
          sms: !!payload.negativeBalanceAlert.sms,
          email: !!payload.negativeBalanceAlert.email,
        };
      }
      if (payload.graceUsd && typeof payload.graceUsd === 'object') {
        state.graceUsd = {
          sms: num(payload.graceUsd.sms, state.graceUsd.sms),
          email: num(payload.graceUsd.email, state.graceUsd.email),
        };
      }

      state.hasBalance = true;
    },

    setLoading(state, v) { state.loading = !!v; },
    setSubscribed(state, v) { state.subscribed = !!v; },
    setUnsub(state, fn) { state.unsub = (typeof fn === 'function') ? fn : null; },

    // Optimistic local decrement for the current user's OWN send: free first,
    // then paid, clamp >= 0 (pure logic in utils/walletCredits.drawCredits). The
    // next Firestore snapshot reconciles the authoritative number.
    applyOptimisticDraw(state, credits) {
      const next = drawCredits(state, credits);
      state.freeRemainingCredits = next.freeRemainingCredits;
      state.paidCredits = next.paidCredits;
      state.creditsAvailable = next.creditsAvailable;
    },

    // ---- Auto-reload settings (SMS) ----
    setReloadConfig(state, config) { state.reloadConfig = config || null; },

    // ---- Credit activity ledger (SMS) ----
    setActivity(state, rows) { state.activity = Array.isArray(rows) ? rows : []; },
    appendActivity(state, rows) {
      state.activity = state.activity.concat(Array.isArray(rows) ? rows : []);
    },
    setActivityLoading(state, v) { state.activityLoading = !!v; },
    setActivityHasMore(state, v) { state.activityHasMore = !!v; },

    // ---- Auto-reload settings + activity (EMAIL) — parallel to the SMS mutations above ----
    setEmailReloadConfig(state, config) { state.emailReloadConfig = config || null; },
    setEmailActivity(state, rows) { state.emailActivity = Array.isArray(rows) ? rows : []; },
    appendEmailActivity(state, rows) {
      state.emailActivity = state.emailActivity.concat(Array.isArray(rows) ? rows : []);
    },
    setEmailActivityLoading(state, v) { state.emailActivityLoading = !!v; },
    setEmailActivityHasMore(state, v) { state.emailActivityHasMore = !!v; },

    // ---- usage report ----
    setUsageMonth(state, m) { state.usageMonth = m || null; },
    setUsageSummary(state, { channel, summary }) {
      // Replace the whole object so Vue 2 reactivity picks up the per-channel key.
      state.usageSummary = Object.assign({}, state.usageSummary, { [channel]: summary });
    },
    setUsageSummaryLoading(state, v) { state.usageSummaryLoading = !!v; },
    setUsageLog(state, log) { state.usageLog = Object.assign({}, state.usageLog, log); },
    setUsageLogLoading(state, v) { state.usageLogLoading = !!v; },
    resetUsage(state) {
      state.usageMonth = null;
      state.usageSummary = { sms: null, email: null };
      state.usageLog = { rows: [], page: 1, total: 0, hasMore: false, channel: 'sms', feature: null, direction: null };
    },

    reset(state) {
      Object.assign(state, initialState());
    },
  },

  actions: {
    /**
     * Seed the balance over HTTP, then open a real-time Firestore subscription.
     * Idempotent: a second call for the SAME site is a no-op; a call for a
     * DIFFERENT site (site switch) tears the old subscription down first.
     */
    async initBalance({ commit, state, dispatch, rootState }, siteID) {
      if (!siteID) return;
      if (state.subscribed) {
        if (state.siteID === siteID) return;    // already live for this site
        dispatch('teardownBalance');            // switched sites → drop the old sub
      }
      commit('setSiteID', siteID);

      // 1) HTTP seed so the pill shows a number immediately (before the snapshot lands).
      commit('setLoading', true);
      try {
        const resp = await axios.post('/get-credit-balance', { siteID });
        if (resp && resp.data) commit('setBalance', resp.data);
      } catch (err) {
        // A site without a wallet (or a transient error) simply leaves the pill hidden.
        console.log('[wallet] get-credit-balance failed:', (err && err.message) || err);
      } finally {
        commit('setLoading', false);
      }

      // 2) Real-time subscription — MIRRORS conversationsV2/initRoomsListener:
      //    modular Firebase handles off rootState; unsubscribe stored + registered
      //    with customer so logout's clearFirestoreListeners tears it down too.
      try {
        if (state.subscribed) return; // re-check after the awaits above
        const { doc, db } = rootState;
        if (!db || !doc) {
          console.error('[wallet] Firestore handles missing from rootState');
          return;
        }
        const ref = doc(db, 'siteWallet', siteID);
        const unsubscribe = safeSnapshot(ref, (snap) => {
          try {
            const data = (snap && typeof snap.data === 'function') ? snap.data() : null;
            if (data) commit('setBalance', data);
          } catch (e) {
            console.error('[wallet] snapshot handler error:', e);
          }
        }, {
          source: 'wallet/balance',
          onError: (error) => {
            console.error('[wallet] onSnapshot error:', error);
          },
        });
        commit('setUnsub', unsubscribe);
        commit('setSubscribed', true);
        commit('customer/addFirestoreListener', unsubscribe, { root: true });
      } catch (err) {
        console.error('[wallet] Failed to open wallet subscription:', err);
      }
    },

    // Detach the subscription and reset. Called on logout + site switch + layout teardown.
    teardownBalance({ commit, state }) {
      try { if (typeof state.unsub === 'function') state.unsub(); } catch (_) {}
      commit('reset');
    },

    /**
     * Purchase paid credits (My Account → Credits tab). `product` selects the pool
     * ('sms' default | 'email'); omitting it is byte-identical to the SMS-only behavior.
     *
     * POST /buy-credits { siteID, amountUsd, purchaseId, product }. purchaseId is a
     * per-click idempotency key (a double-click / retry with the SAME id must
     * not double-charge). On 200 the backend returns the authoritative balance
     * summary → commit setBalance (the SAME free-first-then-paid mutation the
     * HTTP seed and Firestore snapshot use, so there is one source of truth).
     * On 402 (no_card / declined) surface reason + message so the tab can nudge
     * the user to add a card.
     */
    async buyCredits({ commit, state }, { amountUsd, purchaseId, product = 'sms' } = {}) {
      try {
        const resp = await axios.post('/buy-credits', {
          siteID: state.siteID,
          amountUsd,
          purchaseId,
          product,
        });
        const data = (resp && resp.data) || {};
        // The backend returns the authoritative full summary (top-level SMS pool +
        // channels.{sms,email}); setBalance merges it, so an email buy updates
        // channels.email without disturbing the flat SMS fields the pill reads.
        if (data.balance) commit('setBalance', data.balance);
        return { ok: true, invoiceId: data.invoiceId, balance: data.balance };
      } catch (err) {
        // 402 { ok:false, reason, message } is the expected "couldn't charge"
        // shape; anything else falls back to a generic retry message.
        const data = (err && err.response && err.response.data) || {};
        return {
          ok: false,
          reason: data.reason || 'error',
          message: data.message || (err && err.message) || 'Purchase failed. Please try again.',
        };
      }
    },

    /**
     * Open the Stripe hosted billing portal (add/update card, view invoices).
     * POST /billing-portal { siteID, returnUrl } → { url }; redirect the browser
     * to it. returnUrl brings the user back to the SMS Credits tab.
     */
    async openBillingPortal({ state }, { auth } = {}) {
      try {
        // Same call as My Account → Billing (api/billing.js): the server now requires the caller's Firebase token and
        // an Admin of this site, and opens the practice's own no-cancel portal. Only the return tab differs.
        const url = await openPortalUrl({
          auth,
          siteID: state.siteID,
          returnUrl: window.location.origin + '/app/my-account?tab=credits',
        });
        window.location.href = url;
        return { ok: true, url };
      } catch (err) {
        return { ok: false, message: billingErrorMessage(err) };
      }
    },

    /**
     * Load the site's auto-reload settings (My Account → Credits). `product`
     * ('sms' default | 'email') selects the pool and which config mutation is
     * committed (setReloadConfig vs setEmailReloadConfig).
     * POST /get-reload-settings { siteID, product } → { ok, config }. On ok, commit
     * the config so the tab pre-fills its inputs and the low-balance banner can key
     * off `reloadConfig.enabled`. Same non-throwing shape as buyCredits.
     */
    async getReloadSettings({ commit, state }, { product = 'sms' } = {}) {
      try {
        const resp = await axios.post('/get-reload-settings', { siteID: state.siteID, product });
        const data = (resp && resp.data) || {};
        if (data.ok && data.config) {
          commit(product === 'email' ? 'setEmailReloadConfig' : 'setReloadConfig', data.config);
        }
        return { ok: !!data.ok, config: data.config };
      } catch (err) {
        const data = (err && err.response && err.response.data) || {};
        return {
          ok: false,
          message: data.message || (err && err.message) || 'Could not load auto-reload settings.',
        };
      }
    },

    /**
     * Save auto-reload settings. POST /save-reload-settings
     * { siteID, product, autoReloadEnabled, reloadTriggerUsd, reloadAmountUsd, reloadMonthlyCapUsd }
     * → { ok, config }. `product` ('sms' default | 'email') selects the pool + config
     * mutation. A null field is cleared to the env default server-side; the server
     * validates (trigger 1–500, amount 5–1000, cap ≥ amount & ≤ 10000) and 400s on bad
     * input. On ok, commit the normalized config the server echoes.
     */
    async saveReloadSettings({ commit, state }, { product = 'sms', ...payload } = {}) {
      try {
        const resp = await axios.post('/save-reload-settings', { siteID: state.siteID, product, ...payload });
        const data = (resp && resp.data) || {};
        if (data.ok && data.config) {
          commit(product === 'email' ? 'setEmailReloadConfig' : 'setReloadConfig', data.config);
        }
        return { ok: !!data.ok, config: data.config };
      } catch (err) {
        const data = (err && err.response && err.response.data) || {};
        return {
          ok: false,
          message: data.message || (err && err.message) || 'Could not save auto-reload settings.',
        };
      }
    },

    /**
     * Load a page of credit activity. POST /get-credit-activity
     * { siteID, limit, offset, product } → { ok, rows, hasMore }. `product`
     * ('sms' default | 'email') selects the pool + which ledger mutations commit.
     * offset 0 REPLACES the ledger (initial load / refresh); offset > 0 APPENDS
     * (Load more). Loading flag is always cleared, even on error.
     */
    async getCreditActivity({ commit, state }, { limit = 25, offset = 0, product = 'sms' } = {}) {
      const isEmail = product === 'email';
      commit(isEmail ? 'setEmailActivityLoading' : 'setActivityLoading', true);
      try {
        const resp = await axios.post('/get-credit-activity', {
          siteID: state.siteID,
          limit,
          offset,
          product,
        });
        const data = (resp && resp.data) || {};
        const rows = Array.isArray(data.rows) ? data.rows : [];
        if (offset > 0) commit(isEmail ? 'appendEmailActivity' : 'appendActivity', rows);
        else commit(isEmail ? 'setEmailActivity' : 'setActivity', rows);
        commit(isEmail ? 'setEmailActivityHasMore' : 'setActivityHasMore', !!data.hasMore);
        return { ok: !!data.ok, rows, hasMore: !!data.hasMore };
      } catch (err) {
        const data = (err && err.response && err.response.data) || {};
        return {
          ok: false,
          message: data.message || (err && err.message) || 'Could not load credit activity.',
        };
      } finally {
        commit(isEmail ? 'setEmailActivityLoading' : 'setActivityLoading', false);
      }
    },

    /**
     * Usage breakdown for a billing month. POST /get-site-usage-summary
     * { siteID, channel, month } → { creditsUsed, balance, breakdown }.
     *
     * `month` null = the current billing month, and ONLY then does the backend return `balance` —
     * the reconciling "X of 5,000 remaining" line, computed by the same function that feeds the
     * header pill. Pass an explicit 'YYYY-MM' to look back; balance comes back null there because a
     * past month has no meaningful "remaining".
     */
    async getUsageSummary({ commit, state }, { channel = 'sms', month = null } = {}) {
      // Boundary guard: this action POSTs state.siteID — the WALLET store's siteID, seeded ONLY by
      // initBalance. A caller can reach us before that (SmsCreditsTab gates on the CUSTOMER siteID +
      // hasBalance, and reloadUsage also fires from the month <select> and the emailChannel watcher),
      // so during a My Account refresh storm or a site-switch teardown (which commits `reset`) siteID
      // is null and the backend 400s "siteID is required" (prod Lumigo alert 2026-09-02). Refuse the
      // POST, keep the loading flag false (never stuck true — the skip runs BEFORE we set it true),
      // and return the SAME { ok:false } failure shape the catch below does so callers render their
      // existing "could not load" state, NOT a silent empty section (the 2026-07-20 lost-fetch bug).
      if (!state.siteID) {
        console.warn('[wallet] getUsageSummary skipped — wallet siteID not seeded yet');
        commit('setUsageSummaryLoading', false);
        return { ok: false, message: 'Could not load usage.' };
      }
      commit('setUsageSummaryLoading', true);
      try {
        const resp = await axios.post('/get-site-usage-summary', {
          siteID: state.siteID,
          channel,
          month: month || undefined,
        });
        const data = (resp && resp.data) || {};
        const summary = {
          creditsUsed: Number(data.creditsUsed) || 0,
          balance: data.balance || null,
          breakdown: Array.isArray(data.breakdown) ? data.breakdown : [],
          // Outbound-to-Puerto-Rico sub-total (SMS only; {0,0} for email). Additive field — the
          // Usage section renders a "Puerto Rico numbers" line only when pr.messages > 0.
          pr: (data.pr && typeof data.pr === 'object')
            ? { messages: Number(data.pr.messages) || 0, credits: Number(data.pr.credits) || 0 }
            : { messages: 0, credits: 0 },
        };
        commit('setUsageSummary', { channel, summary });
        return { ok: true, summary };
      } catch (err) {
        const data = (err && err.response && err.response.data) || {};
        commit('setUsageSummary', { channel, summary: null });
        return { ok: false, message: data.message || (err && err.message) || 'Could not load usage.' };
      } finally {
        commit('setUsageSummaryLoading', false);
      }
    },

    /**
     * One page of the message log. POST /get-site-usage-log
     * { siteID, channel, month, feature?, direction?, page, limit } → { rows, total, hasMore }.
     *
     * Server-paged deliberately: a campaign month can exceed 40k rows, which would freeze a
     * client-side table. `total` is for the CURRENT filter set — the breakdown totals come from the
     * summary above and are unaffected by paging.
     */
    async getUsageLog({ commit, state }, { channel = 'sms', month = null, feature = null, direction = null, page = 1, limit = 50 } = {}) {
      // Boundary guard — same hole as getUsageSummary: no seeded wallet siteID → the endpoint 400s.
      // Skip the POST, keep the loading flag false, and return the existing failure shape.
      if (!state.siteID) {
        console.warn('[wallet] getUsageLog skipped — wallet siteID not seeded yet');
        commit('setUsageLogLoading', false);
        return { ok: false, message: 'Could not load the message log.' };
      }
      commit('setUsageLogLoading', true);
      try {
        const resp = await axios.post('/get-site-usage-log', {
          siteID: state.siteID,
          channel,
          month: month || undefined,
          feature: feature || undefined,
          direction: direction || undefined,
          page,
          limit,
        });
        const data = (resp && resp.data) || {};
        commit('setUsageLog', {
          rows: Array.isArray(data.rows) ? data.rows : [],
          page: Number(data.page) || page,
          total: Number(data.total) || 0,
          hasMore: !!data.hasMore,
          channel,
          feature,
          direction,
        });
        return { ok: true, rows: data.rows || [], total: data.total || 0 };
      } catch (err) {
        const data = (err && err.response && err.response.data) || {};
        commit('setUsageLog', { rows: [], page: 1, total: 0, hasMore: false, channel, feature, direction });
        return { ok: false, message: data.message || (err && err.message) || 'Could not load the message log.' };
      } finally {
        commit('setUsageLogLoading', false);
      }
    },
  },
};
